// client/src/api/services/subscription.ts
import { apiClient } from '../client';
import { ENDPOINTS, buildQueryString } from '../endpoints';
import { getErrorMessage } from '@/utils/errorUtils';
import { AxiosError } from "axios";

// ==================== TYPES ====================
export interface ActiveSubscription {
  subscriptionId: string;
  plan: {
    id: string;
    name: string;
    period: 'day' | 'week' | 'month';
  };
  status: 'active' | 'cancelled' | 'expired';
  startDate: string;
  endDate: string;
  dataUsage: {
    used: number;
    total: number;
    unit: string;
    percentage: number;
  };
  autoRenew: boolean;
  // T-04 fields
  price_minor?: number;
  currency?: string;
  country_code?: string;
  duration_days?: number;
  gross_paid_minor?: number;
  tax_minor?: number;
}

export interface Connection {
  connectionId: string;
  hotspot: {
    id: string;
    name: string;
  };
  connectedAt: string;
  disconnectedAt: string | null;
  duration: number;
  dataUsed: number;
  dataUsedUnit: string;
  status: 'active' | 'completed' | 'interrupted';
}

export interface Device {
  deviceId: string;
  name: string;
  type: 'mobile' | 'laptop' | 'tablet' | 'desktop';
  macAddress: string;
  lastConnected: string;
  isActive: boolean;
}

export interface SubscriptionResponse<T> {
  success: boolean;
  data: T;
}

export interface CancelSubscriptionRequest {
  subscriptionId: string;
  reason: string;
  cancelImmediately: boolean;
}

// ==================== API CALLS ====================

/**
 * Get active subscription
 */
export const getActiveSubscription = async (): Promise<SubscriptionResponse<ActiveSubscription | null>> => {
  try {
    const response = await apiClient.get<SubscriptionResponse<ActiveSubscription>>(
      ENDPOINTS.SUBSCRIPTION.ACTIVE
    );
    return response.data;
  } catch (error: unknown) {
    if ((error as AxiosError).response?.status === 404 || getErrorMessage(error) === 'NO_ACTIVE_SUBSCRIPTION') {
      return {
        success: true,
        data: null
      };
    }
    console.error('Error fetching active subscription:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch active subscription');
  }
};

/**
 * Cancel subscription
 */
export const cancelSubscription = async (
  subscriptionId: string, 
  reason?: string, 
  cancelImmediately: boolean = false
): Promise<SubscriptionResponse<unknown>> => {
  try {
    const payload: CancelSubscriptionRequest = {
      subscriptionId,
      reason: reason || 'No reason provided',
      cancelImmediately
    };

    const response = await apiClient.post<SubscriptionResponse<unknown>>(
      ENDPOINTS.SUBSCRIPTION.CANCEL(subscriptionId),
      payload
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Error cancelling subscription:', error);
    throw new Error(getErrorMessage(error) || 'Failed to cancel subscription');
  }
};

/**
 * Toggle auto-renew
 */
export const toggleAutoRenew = async (
  subscriptionId: string, 
  autoRenew: boolean
): Promise<SubscriptionResponse<{ subscriptionId: string; autoRenew: boolean }>> => {
  try {
    const response = await apiClient.patch<SubscriptionResponse<{ subscriptionId: string; autoRenew: boolean }>>(
      ENDPOINTS.SUBSCRIPTION.AUTO_RENEW(subscriptionId),
      { autoRenew }
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Error toggling auto-renew:', error);
    throw new Error(getErrorMessage(error) || 'Failed to toggle auto-renew');
  }
};

/**
 * Get connection history
 */
export const getConnectionHistory = async (
  page: number = 1, 
  limit: number = 10
): Promise<SubscriptionResponse<{ connections: Connection[]; pagination: unknown }>> => {
  try {
    const queryString = buildQueryString({ page, limit });
    const response = await apiClient.get<SubscriptionResponse<{ connections: Connection[]; pagination: unknown }>>(
      `${ENDPOINTS.SUBSCRIPTION.CONNECTION_HISTORY}${queryString}`
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching connection history:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch connection history');
  }
};

/**
 * Get connected devices
 */
export const getConnectedDevices = async (): Promise<SubscriptionResponse<{
  devices: Device[];
  maxDevices: number;
  currentDevices: number;
}>> => {
  try {
    const response = await apiClient.get<SubscriptionResponse<{
      devices: Device[];
      maxDevices: number;
      currentDevices: number;
    }>>(
      ENDPOINTS.SUBSCRIPTION.DEVICES
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching connected devices:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch connected devices');
  }
};

/**
 * Upgrade subscription plan
 */
export const upgradeSubscription = async (
  subscriptionId: string,
  newPlanId: string
): Promise<SubscriptionResponse<unknown>> => {
  try {
    const response = await apiClient.post<SubscriptionResponse<unknown>>(
      ENDPOINTS.SUBSCRIPTION.UPGRADE(subscriptionId),
      { newPlanId }
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Error upgrading subscription:', error);
    throw new Error(getErrorMessage(error) || 'Failed to upgrade subscription');
  }
};

/**
 * Get subscription management details
 */
export const getSubscriptionManagement = async (): Promise<SubscriptionResponse<unknown>> => {
  try {
    const response = await apiClient.get<SubscriptionResponse<unknown>>(
      ENDPOINTS.SUBSCRIPTION.MANAGE
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching subscription management:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fetch subscription management');
  }
};

export default {
  getActiveSubscription,
  cancelSubscription,
  toggleAutoRenew,
  getConnectionHistory,
  getConnectedDevices,
  upgradeSubscription,
  getSubscriptionManagement,
};
